/**
 * FAVORITE SELLERS PAGE
 * 
 * Shows all sellers the user has favorited.
 * Features:
 * - Grid display of favorite sellers
 * - Link to their profile
 * - Link to their marketplace listings
 * - Remove from favorites
 * - Stats (items listed, vouches)
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, doc, getDoc, deleteDoc, setDoc, query, where } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import {
  Heart, Trash2, Store, Shield,
  Loader2, ArrowLeft, User, ShoppingBag
} from 'lucide-react';

interface FavoriteSeller {
  id: string;
  sellerId: string;
  sellerUsername: string;
  sellerAvatar: string | null;
  addedAt: any;
  // Live data
  displayName?: string;
  bio?: string;
  destashCount?: number;
  vouchCount?: number;
}

const FavoriteSellers: React.FC = () => {
  const [favorites, setFavorites] = useState<FavoriteSeller[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  // Listen for auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUserId(user?.uid || null);
      if (user) {
        loadFavorites(user.uid);
      } else {
        setFavorites([]);
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // Load favorite sellers
  const loadFavorites = async (userId: string) => {
    setLoading(true);
    try {
      const favoritesRef = collection(db, 'users', userId, 'favoriteSellers');
      const snapshot = await getDocs(favoritesRef);
      
      // First pass: collect all favorites and identify duplicates
      const sellerMap = new Map<string, { docId: string; data: any; isCorrectId: boolean }[]>();
      
      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        const sellerId = data.sellerId;
        
        if (!sellerMap.has(sellerId)) {
          sellerMap.set(sellerId, []);
        }
        
        sellerMap.get(sellerId)!.push({
          docId: docSnap.id,
          data,
          isCorrectId: docSnap.id === sellerId // Document ID should match sellerId
        });
      }
      
      // Clean up duplicates in Firestore (delete docs where docId !== sellerId)
      const cleanupPromises: Promise<void>[] = [];
      for (const [sellerId, entries] of sellerMap) {
        if (entries.length > 1) {
          // Multiple entries for same seller - keep the one where docId === sellerId
          for (const entry of entries) {
            if (!entry.isCorrectId) {
              cleanupPromises.push(
                deleteDoc(doc(db, 'users', userId, 'favoriteSellers', entry.docId))
              );
            }
          }
        } else if (entries.length === 1 && !entries[0].isCorrectId) {
          // Single entry but with wrong docId - migrate it
          const entry = entries[0];
          cleanupPromises.push(
            (async () => {
              // Create new doc with correct ID
              await setDoc(doc(db, 'users', userId, 'favoriteSellers', sellerId), entry.data);
              // Delete old doc
              await deleteDoc(doc(db, 'users', userId, 'favoriteSellers', entry.docId));
            })()
          );
          // Update the entry so we use the correct ID going forward
          entry.docId = sellerId;
          entry.isCorrectId = true;
        }
      }
      
      // Run cleanup in background (don't block loading)
      if (cleanupPromises.length > 0) {
        Promise.all(cleanupPromises).catch(err => 
          console.error('Error cleaning up duplicate favorites:', err)
        );
      }
      
      // Second pass: build deduplicated sellers list
      const sellers: FavoriteSeller[] = [];
      
      for (const [sellerId, entries] of sellerMap) {
        // Use the entry with correct docId, or first entry if none are correct
        const bestEntry = entries.find(e => e.isCorrectId) || entries[0];
        const data = bestEntry.data;
        
        const seller: FavoriteSeller = {
          id: bestEntry.docId,
          sellerId: sellerId,
          sellerUsername: data.sellerUsername || '',
          sellerAvatar: data.sellerAvatar || null,
          addedAt: data.addedAt,
        };

        // Fetch current seller info
        try {
          const sellerRef = doc(db, 'users', sellerId);
          const sellerDoc = await getDoc(sellerRef);
          
          if (sellerDoc.exists()) {
            const sellerData = sellerDoc.data();
            seller.sellerUsername = sellerData.username || seller.sellerUsername;
            seller.sellerAvatar = sellerData.avatarUrl || seller.sellerAvatar;
            seller.displayName = sellerData.displayName || sellerData.profile?.displayName;
            seller.bio = sellerData.bio || sellerData.profile?.bio;
          }
        } catch (err) {
          console.error('Error fetching seller info:', err);
        }

        // Count destash items
        try {
          const itemsRef = collection(db, 'users', sellerId, 'items');
          const destashQuery = query(itemsRef, where('isForDestash', '==', true));
          const destashSnapshot = await getDocs(destashQuery);
          seller.destashCount = destashSnapshot.size;
        } catch (err) {
          seller.destashCount = 0;
        }

        // Count vouches
        try {
          const vouchesRef = collection(db, 'users', sellerId, 'vouches');
          const vouchSnapshot = await getDocs(vouchesRef);
          seller.vouchCount = vouchSnapshot.size;
        } catch (err) {
          seller.vouchCount = 0;
        }

        sellers.push(seller);
      }

      // Sort by username
      sellers.sort((a, b) => (a.sellerUsername || '').localeCompare(b.sellerUsername || ''));
      
      setFavorites(sellers);
    } catch (err) {
      console.error('Error loading favorites:', err);
    } finally {
      setLoading(false);
    }
  };

  // Remove from favorites
  const handleRemove = async (favoriteId: string) => {
    if (!currentUserId) return;
    
    setRemovingId(favoriteId);
    try {
      await deleteDoc(doc(db, 'users', currentUserId, 'favoriteSellers', favoriteId));
      setFavorites(prev => prev.filter(f => f.id !== favoriteId));
    } catch (err) {
      console.error('Error removing favorite:', err);
      alert('Failed to remove. Please try again.');
    } finally {
      setRemovingId(null);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-glass animate-spin mx-auto mb-4" />
          <p className="text-glass-secondary font-body">Loading your favorite sellers...</p>
        </div>
      </div>
    );
  }

  // Not logged in
  if (!currentUserId) {
    return (
      <div className="min-h-screen py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="glass-card p-12 text-center">
            <Heart className="w-16 h-16 text-gray-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-glass mb-2 font-heading">
              Sign In to See Your Favorites
            </h2>
            <p className="text-glass-secondary mb-4 font-body">
              Keep track of your favorite sellers
            </p>
            <Link
              to="/login"
              className="inline-block px-6 py-3 bg-slate-700 text-white rounded-lg font-bold hover:bg-slate-800 transition-default font-body"
            >
              Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header - GLASSMORPHIC */}
        <div className="glass-card p-6 mb-6">
          <div className="flex items-center gap-4">
            <Link
              to="/marketplace"
              className="p-2 hover:bg-slate-800/60 rounded-lg transition-default"
            >
              <ArrowLeft className="icon-base text-glass-secondary" />
            </Link>
            
            <div className="flex-1">
              <h1 className="text-3xl text-glass font-heading">
                Favorite Sellers
              </h1>
              <p className="text-glass-secondary font-body">
                Sellers you love to shop from
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              <Heart className="icon-lg text-red-500 fill-current" />
            </div>
          </div>

          {/* Stats */}
          {favorites.length > 0 && (
            <div className="mt-4 p-4 bg-slate-800/60 rounded-xl text-center">
              <p className="text-2xl font-bold text-glass">{favorites.length}</p>
              <p className="text-sm text-glass-muted">Favorite Seller{favorites.length !== 1 ? 's' : ''}</p>
            </div>
          )}
        </div>

        {/* Empty State */}
        {favorites.length === 0 && (
          <div className="glass-card p-12 text-center">
            <Heart className="w-16 h-16 text-gray-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-glass mb-2 font-heading">
              No Favorite Sellers Yet
            </h2>
            <p className="text-glass-secondary mb-4 font-body">
              Click the heart icon on seller profiles or marketplace items to add them here
            </p>
            <Link
              to="/marketplace"
              className="inline-block px-6 py-3 bg-slate-700 text-white rounded-lg font-bold hover:bg-slate-800 transition-default font-body"
            >
              Browse Marketplace
            </Link>
          </div>
        )}

        {/* Sellers Grid */}
        {favorites.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {favorites.map((seller) => (
              <div
                key={seller.id}
                className="glass-card overflow-hidden hover:border-purple-500/50 transition-all"
              >
                {/* Header with Avatar */}
                <div className="relative h-24 rainbow-gradient">
                  {/* Remove Button */}
                  <button
                    onClick={() => handleRemove(seller.id)}
                    disabled={removingId === seller.id}
                    className="absolute top-2 right-2 p-2 bg-slate-800/80 rounded-full shadow hover:bg-red-500/20 hover:text-red-400 transition-default"
                    title="Remove from favorites"
                  >
                    {removingId === seller.id ? (
                      <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                    ) : (
                      <Trash2 className="w-4 h-4 text-slate-300" />
                    )}
                  </button>

                  {/* Avatar */}
                  <div className="absolute -bottom-10 left-4">
                    {seller.sellerAvatar ? (
                      <img
                        src={seller.sellerAvatar}
                        alt={seller.sellerUsername}
                        className="w-20 h-20 rounded-full object-cover border-4 border-slate-700 shadow-lg"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-full rainbow-gradient border-4 border-slate-700 shadow-lg flex items-center justify-center">
                        <span className="text-white text-2xl font-bold">
                          {(seller.sellerUsername || '?').charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Info */}
                <div className="pt-12 pb-4 px-4">
                  {/* Name & Username */}
                  <div className="mb-2">
                    {seller.displayName && (
                      <h3 className="text-lg font-bold text-glass font-body">
                        {seller.displayName}
                      </h3>
                    )}
                    <p className="text-glass-secondary font-medium font-body">
                      @{seller.sellerUsername}
                    </p>
                  </div>

                  {/* Bio */}
                  {seller.bio && (
                    <p className="text-sm text-glass-muted mb-3 line-clamp-2 font-body">
                      {seller.bio}
                    </p>
                  )}

                  {/* Stats */}
                  <div className="flex items-center gap-4 mb-4 text-sm text-glass-muted">
                    <div className="flex items-center gap-1">
                      <Store className="icon-sm" />
                      <span>{seller.destashCount || 0} listed</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Shield className="icon-sm" />
                      <span>{seller.vouchCount || 0} vouches</span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <Link
                      to={`/profile/${seller.sellerUsername}`}
                      className="flex-1 py-2 px-3 border-2 border-slate-600 text-glass-secondary rounded-lg font-bold text-sm hover:bg-slate-700 hover:text-white transition-default flex items-center justify-center gap-2 font-body"
                    >
                      <User className="icon-sm" />
                      Profile
                    </Link>
                    <Link
                      to={`/marketplace?seller=${seller.sellerUsername}`}
                      className="flex-1 py-2 px-3 bg-slate-700 text-white rounded-lg font-bold text-sm hover:bg-slate-800 transition-default flex items-center justify-center gap-2 font-body"
                    >
                      <ShoppingBag className="icon-sm" />
                      Shop
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FavoriteSellers;
